import { useState, useEffect } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  User, 
  Shield, 
  Bell, 
  Palette, 
  Key, 
  Lock, 
  Settings as SettingsIcon,
  AlertCircle,
  RefreshCw
} from 'lucide-react'
import { ProfileSettings } from '@/components/settings/ProfileSettings'
import { SecuritySettings } from '@/components/settings/SecuritySettings'
import { NotificationSettings } from '@/components/settings/NotificationSettings'
import { AppearanceSettings } from '@/components/settings/AppearanceSettings'
import { APISettings } from '@/components/settings/APISettings'
import { PrivacySettings } from '@/components/settings/PrivacySettings'
import { SystemSettings } from '@/components/settings/SystemSettings'
import { toast } from 'sonner'

export function SettingsPage() {
  const { loadSettings, loading, error } = useSettingsStore()
  const [activeTab, setActiveTab] = useState('profile')

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const tabs = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      description: 'Manage your personal information and preferences'
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      description: 'Password, 2FA, and session management'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Email alerts and notification preferences'
    },
    {
      id: 'appearance',
      label: 'Appearance',
      icon: Palette,
      description: 'Theme, language, and display settings'
    },
    {
      id: 'api',
      label: 'API & Keys',
      icon: Key,
      description: 'API keys and webhook management'
    },
    {
      id: 'privacy',
      label: 'Privacy',
      icon: Lock,
      description: 'Data sharing and privacy controls'
    },
    {
      id: 'system',
      label: 'System',
      icon: SettingsIcon,
      description: 'Default preferences and system settings'
    }
  ]

  if (loading && !error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
          <span className="text-muted-foreground">Loading settings...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account settings and preferences
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Settings Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Settings Categories</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  const isActive = activeTab === tab.id
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left text-sm transition-colors rounded-none ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <div className="flex-1">
                        <div className="font-medium">{tab.label}</div>
                        <div className={`text-xs ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                          {tab.description}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="space-y-6">
            {/* Active Tab Header */}
            <div className="flex items-center space-x-3">
              {(() => {
                const currentTab = tabs.find(tab => tab.id === activeTab)
                if (!currentTab) return null
                const Icon = currentTab.icon
                return (
                  <>
                    <Icon className="h-6 w-6 text-primary" />
                    <div>
                      <h2 className="text-xl font-semibold">{currentTab.label}</h2>
                      <p className="text-sm text-muted-foreground">{currentTab.description}</p>
                    </div>
                  </>
                )
              })()}
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
              {activeTab === 'profile' && <ProfileSettings />}
              {activeTab === 'security' && <SecuritySettings />}
              {activeTab === 'notifications' && <NotificationSettings />}
              {activeTab === 'appearance' && <AppearanceSettings />}
              {activeTab === 'api' && <APISettings />}
              {activeTab === 'privacy' && <PrivacySettings />}
              {activeTab === 'system' && <SystemSettings />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 